import { useEffect, useMemo } from 'react';

import { errorApiRef, useApi } from '@backstage/core-plugin-api';

import RefreshIcon from '@mui/icons-material/Refresh';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { useStyles as useSharedStyles } from '../styles';
import { KaapTenant } from '../types';
import { useGetKaapTenants } from '../useGetKaapTenants';
import { PimStatusChip } from './PimStatusChip';

const useStyles = makeStyles()(theme => ({
  pimOptionChip: {
    borderColor: theme.palette.secondary.main,
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.caption.fontSize,
    height: 18,
    minWidth: 0,
  },
  refreshButton: {
    whiteSpace: 'nowrap',
  },
}));

export interface KaapTenantPickerProps {
  formData?: KaapTenant | string;
  onChange: (value: KaapTenant | string) => void;
  rawErrors?: string[];
  title?: string;
  description?: string;
  /** `'object'` emits the whole tenant; `'string'` emits its id. */
  type?: 'object' | 'string';
  required?: boolean;
  id?: string;
}

/**
 * Picks a KaaP tenant to bind a DaaS project cluster to.
 *
 * Tenants come from the KaaP API rather than the catalog because only the API
 * reports PIM provisioning state, and a tenant whose PIM group is not ready yet
 * fails `clusterInputErrors`. The refresh button re-polls that state in place.
 */
export const KaapTenantPicker = (props: KaapTenantPickerProps) => {
  const {
    formData,
    onChange,
    rawErrors,
    title = '',
    description = '',
    type = 'string',
    required,
    id,
  } = props;

  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();
  const errorApi = useApi(errorApiRef);

  const [{ value: tenants, loading, error }, fetchTenants] =
    useGetKaapTenants();

  useEffect(() => {
    void fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [error, errorApi]);

  const tenantOptions = useMemo(() => tenants || [], [tenants]);

  const selectedOption = useMemo(() => {
    if (!formData) return null;

    if (typeof formData === 'string') {
      return tenantOptions.find(option => option.id === formData) || null;
    }

    return (
      tenantOptions.find(option => option.id === formData.id) ??
      formData ??
      null
    );
  }, [formData, tenantOptions]);

  // A PIM group provisioned after the project form was filled in only reaches
  // the form through a refetch, so re-emit the tenant once it appears.
  useEffect(() => {
    if (
      selectedOption &&
      typeof formData === 'object' &&
      selectedOption.id === formData.id &&
      selectedOption.pimGroupId !== formData.pimGroupId
    ) {
      onChange(type === 'object' ? selectedOption : selectedOption.id ?? '');
    }
  }, [selectedOption, formData, onChange, type]);

  const onSelect = (_: unknown, value: KaapTenant | null) => {
    if (!value) {
      onChange('');
      return;
    }

    onChange(type === 'object' ? value : value.id ?? '');
  };

  const showRefreshButton = Boolean(
    selectedOption?.pimEnabled && !selectedOption?.pimGroupId,
  );
  const hasErrors = Boolean(rawErrors?.length);

  return (
    <Box className={sharedClasses.flexContainer}>
      <Box className={sharedClasses.inputContainer}>
        <FormControl
          fullWidth
          required={required}
          error={hasErrors && !formData}
        >
          <Autocomplete
            size="small"
            id={id}
            value={selectedOption}
            onChange={onSelect}
            options={tenantOptions}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                label={title}
                margin="none"
                helperText={description}
                variant="outlined"
                required={required}
                error={hasErrors}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <PimStatusChip
                          pimEnabled={selectedOption?.pimEnabled}
                          pimGroupId={selectedOption?.pimGroupId}
                          loading={loading}
                        />
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={({ key, ...optionProps }, option) => (
              <li key={key} {...optionProps}>
                <Box>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" style={{fontWeight: 600}} noWrap>
                      {option.name}
                    </Typography>
                    {option.pimEnabled && (
                      <Chip
                        label="PIM enabled"
                        size="small"
                        variant="outlined"
                        className={classes.pimOptionChip}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" noWrap color="text.secondary">
                    Cluster ID: {option.clusterId}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No tenants found"
            loadingText="Loading tenants..."
          />
          {rawErrors?.map((err, index) => (
            <FormHelperText key={index} error>
              {err}
            </FormHelperText>
          ))}
        </FormControl>
      </Box>
      {showRefreshButton && (
        <Box marginBottom={2}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={() => fetchTenants()}
            disabled={loading}
            className={classes.refreshButton}
          >
            {loading ? 'Checking...' : 'Check again'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
